// Unit handling for the broker wire (presentation only — never arithmetic
// on money).
//
// Broker money is CANONICAL 8dp (1 GOLD = "100000000"); every money
// component this app shares with the other consoles (the SM-443 currency
// contract, <CurrencyAmount>) takes the public 4dp wire integer. Moving
// between the two is a decimal-point shift done on the digit string, the
// same rule web-backoffice's broker screens use (canonToWire): truncate
// toward zero, keep the sign, never round, never go through a float.
//
// Rates are integer strings at a precision (broker plans: 6, "10000" =
// 1 %); shares are basis points. Both are shown as percentages by the
// same digit shift.
//
// Relative imports only: node --test runs this file directly.

const CANON_DP = 8;
const WIRE_DP = 4;

function isIntString(s: string): boolean {
  return /^-?\d+$/.test(s);
}

/** Drop `n` trailing digits of an integer string (truncation toward zero). */
function shiftDown(int: string, n: number): string {
  const negative = int.startsWith("-");
  const abs = negative ? int.slice(1) : int;
  const kept = abs.length > n ? abs.slice(0, abs.length - n) : "0";
  const norm = kept.replace(/^0+(?=\d)/, "");
  return negative && norm !== "0" ? `-${norm}` : norm;
}

/**
 * A canonical 8dp amount as the 4dp wire integer every money component
 * takes. undefined for anything that is not an integer string, so a
 * malformed field renders as a placeholder rather than as a wrong number.
 *   canonToWire("100000000") → "10000"   (1 GOLD)
 *   canonToWire("-123456789") → "-12345"
 */
export function canonToWire(canon: string | null | undefined): string | undefined {
  if (canon === null || canon === undefined) return undefined;
  const s = String(canon).trim();
  if (!isIntString(s)) return undefined;
  return shiftDown(s, CANON_DP - WIRE_DP);
}

/**
 * settlement's realized P&L is a PRODUCT unit: raw-lot quantity × price
 * minor, i.e. dollars × 10^(4 + amount_precision). As the 4dp wire
 * integer: drop `amount_precision` digits.
 */
export function pnlProductToWire(pnl: string | null | undefined, amountPrecision: number): string | undefined {
  if (pnl === null || pnl === undefined) return undefined;
  const s = String(pnl).trim();
  if (!isIntString(s)) return undefined;
  const ap = Number.isInteger(amountPrecision) && amountPrecision > 0 ? amountPrecision : 0;
  return shiftDown(s, ap);
}

/**
 * Place a decimal point `dp` digits from the right of an integer string,
 * trimming trailing zeros (and the point) — exact, no float.
 *   shiftDecimal("12345", 2) → "123.45"; shiftDecimal("1200", 2) → "12"
 */
export function shiftDecimal(int: string | null | undefined, dp: number, keep = 0): string {
  if (int === null || int === undefined) return "—";
  const s = String(int).trim();
  if (!isIntString(s)) return "—";
  const negative = s.startsWith("-");
  const abs = (negative ? s.slice(1) : s).replace(/^0+(?=\d)/, "");
  if (dp <= 0) return (negative && abs !== "0" ? "−" : "") + abs;
  const padded = abs.padStart(dp + 1, "0");
  const whole = padded.slice(0, padded.length - dp);
  let frac = padded.slice(padded.length - dp).replace(/0+$/, "");
  if (frac.length < keep) frac = frac.padEnd(keep, "0");
  const body = frac ? `${whole}.${frac}` : whole;
  const isZero = /^0(\.0*)?$/.test(body);
  return (negative && !isZero ? "−" : "") + body;
}

/** A fee rate at `precision` as a percentage string: ("10000", 6) → "1%". */
export function formatRate(rate: string | null | undefined, precision: number): string {
  if (rate === null || rate === undefined || rate === "") return "—";
  // percent = rate × 100 / 10^precision → shift by precision − 2
  const shift = precision - 2;
  if (shift < 0) return `${rate}×10^${-shift}%`;
  const v = shiftDecimal(rate, shift);
  return v === "—" ? v : `${v}%`;
}

/** Basis points as a percentage: 2500 → "25%", 1 → "0.01%". */
export function formatBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return "—";
  return `${shiftDecimal(String(Math.trunc(bps)), 2)}%`;
}

/** A quantity in amount-precision units as human shares. */
export function formatQuantity(qty: string | null | undefined, amountPrecision: number): string {
  return shiftDecimal(qty, amountPrecision > 0 ? amountPrecision : 0);
}

export type ParseResult = { ok: true; value: string; decimals: number } | { ok: false; error: "empty" | "invalid" | "tooPrecise" | "notPositive" };

/**
 * A typed decimal ("12.5", "1,000") as an integer string scaled by
 * 10^scale. Refuses more than `maxDecimals` fraction digits rather than
 * silently truncating what the user typed.
 */
export function parseDecimal(input: string, scale: number, maxDecimals = scale, allowZero = false): ParseResult {
  const raw = input.replace(/[,\s_]/g, "");
  if (raw === "") return { ok: false, error: "empty" };
  const m = /^(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!m) return { ok: false, error: "invalid" };
  const whole = m[1] ?? "";
  const frac = m[2] ?? "";
  if (whole === "" && frac === "") return { ok: false, error: "invalid" };
  if (frac.length > maxDecimals || frac.length > scale) return { ok: false, error: "tooPrecise" };
  const digits = (whole + frac.padEnd(scale, "0")).replace(/^0+(?=\d)/, "");
  if (!allowZero && /^0+$/.test(digits)) return { ok: false, error: "notPositive" };
  return { ok: true, value: digits, decimals: frac.length };
}

/**
 * A typed amount of a currency as the CANONICAL 8dp string a funding
 * command takes. Funding refuses sub-display dust (amount % 10000 == 0,
 * i.e. at most 4 decimals), so more than 4 decimals is refused here too.
 *   parseAmountToCanonical("12.5") → "1250000000"
 */
export function parseAmountToCanonical(input: string): ParseResult {
  return parseDecimal(input, CANON_DP, WIRE_DP);
}

/**
 * A typed price on the 0–1 scale (0.55) at the calculator's price precision 4
 * ("5500"), and a typed share quantity at the fewest decimals that hold it
 * (amount_precision), for the fee preview request.
 */
export function parsePrice(input: string): ParseResult {
  return parseDecimal(input, WIRE_DP, WIRE_DP);
}

export function parseQuantity(input: string, maxDecimals = 4): ParseResult {
  const r = parseDecimal(input, maxDecimals, maxDecimals);
  if (!r.ok) return r;
  // Re-express at exactly the typed decimals: "12.50" → 1250 @ ap 2.
  const scaled = parseDecimal(input, r.decimals, r.decimals);
  return scaled;
}
